import React, { Component } from "react";
import { Card, Form, Input, Steps, Select, TreeSelect, Icon, Alert } from "antd";
import { validate } from "validate.js";
import { urlValidationRule } from "./validators";
import UrlInput from "./UrlInput";
import Competitors from "./Competitors";
import OnboardingStartScreen from "./OnboardingStartScreen";
import "./TrackedWebsitesWizard.less";
import { createReport } from "../../../../store/analysis/operations";
import FirstStepButtons from "./FirstStepButtons";
import IntermediateStepButtons from "./IntermediateStepButtons";
import LastStepButtons from "./LastStepButtons";
import { extractKeywords } from "../../../../utils/keywords";
import { getCookie, removeCookie, defaultIamCookieAttributes } from "../../../../utils/cookie";

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;

const steps = ["Website", "Location", "Competitors", "Keywords"];

class AddTrackedWebsiteWizard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showStartScreen: this.props.showStartScreen,
      current: 0,
      mainUrl: this.getUrlFromCookie(),
      mainUrlSchema: "https://",
      mainUrlValidationError: "",
      mainUrlExistenceError: "",
      isCheckingMainUrlExistence: false,
      seoDb: "us",
      mainLocation: "us-east-1",
      additionalLocations: [],
      competitors: [],
      competitorsValidation: [],
      keywords: ""
    };

    this.getUrlFromCookie = this.getUrlFromCookie.bind(this);
    this.getMainUrl = this.getMainUrl.bind(this);
    this.normalizeUrl = this.normalizeUrl.bind(this);
    this.validateUrl = this.validateUrl.bind(this);
    this.handleSeoDbChange = this.handleSeoDbChange.bind(this);
    this.changeMainLocation = this.changeMainLocation.bind(this);
    this.changeAdditionalLocations = this.changeAdditionalLocations.bind(this);
    this.getTreeData = this.getTreeData.bind(this);
    this.validateCompetitors = this.validateCompetitors.bind(this);
    this.getCompetitors = this.getCompetitors.bind(this);
    this.handleKeywordsChange = this.handleKeywordsChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleStartOnboarding = this.handleStartOnboarding.bind(this);
    this.redirectToDashboard = this.redirectToDashboard.bind(this);
  }

  getUrlFromCookie = () => {
    let url = getCookie("default_url") || "";
    removeCookie("default_url", defaultIamCookieAttributes);
    return url.replace("https://", "").replace("http://", "");
  };

  next = () => {
    if (this.state.current === 0) {
      this.validateUrl();
    }
    if (this.state.current === 1) {
      this.handleSendRequestForLoadingTime();
    }
    if (this.state.current === 2) {
      this.validateCompetitors();
    }
    if (this.state.current === 3) {
      this.validateCompetitors();
    }
  };

  prev = () => {
    const current = this.state.current - 1;
    this.setState({ current });
  };

  redirectToDashboard() {
    this.props.sendOnboardingEvent("skip_onboarding").then(_ => this.props.push("/dashboard"));
  }

  handleSubmit() {
    let mainUrl = this.normalizeUrl(this.state.mainUrl, this.state.mainUrlSchema);
    let competitors = this.state.competitors
      .filter(competitor => competitor["url"])
      .map(competitor => this.normalizeUrl(competitor["url"], competitor["urlSchema"]));
    let uniqueCompetitors = competitors.filter(
      (item, index) => competitors.indexOf(item) === index && item !== mainUrl
    );
    let regions = this.state.mainLocation ? [this.state.mainLocation, ...this.state.additionalLocations] : [];
    let keywordsList = extractKeywords(this.state.keywords);
    let domain = this.state.mainUrl;

    this.setState(prevState => ({
      ...prevState,
      current: prevState.current + 1
    }));

    this.props.handleWizardSubmit(mainUrl, this.state.seoDb, uniqueCompetitors, regions, keywordsList, domain);
  }

  getMainUrl(mainUrl) {
    this.setState({
      mainUrl: mainUrl["url"],
      mainUrlSchema: mainUrl["urlSchema"],
      mainUrlValidationError: "",
      mainUrlExistenceError: "",
      isCheckingMainUrlExistence: false
    });
  }

  normalizeUrl(url, urlSchema) {
    let normalizedUrl = url.toLowerCase().trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      normalizedUrl = urlSchema.concat(url);
    }
    return normalizedUrl;
  }

  validateUrl() {
    this.setState({ mainUrlExistenceError: "", isCheckingMainUrlExistence: false });
    let normalizedUrl = this.normalizeUrl(this.state.mainUrl, this.state.mainUrlSchema);
    const validationError = validate({ url: normalizedUrl }, urlValidationRule);

    if (!validationError) {
      const current = this.state.current + 1;
      this.setState({ mainUrlValidationError: "", isCheckingMainUrlExistence: true });

      this.props.checkDomainsBlackList(normalizedUrl).then(response => {
        if ("status" in response && response["status"] === "error") {
          this.setState({
            mainUrlExistenceError: response["message"],
            isCheckingMainUrlExistence: false
          });
          return;
        } else {
          createReport(normalizedUrl, [], this.props.accessToken).then(response => {
            if ("error" in response) {
              this.setState({
                mainUrlExistenceError:
                  "We cannot access your url in order to perform our test! Either the URL is incorrect, your site is experiencing downtime, or our tool is being blocked by your server.",
                isCheckingMainUrlExistence: false
              });
              return;
            }
            this.setState({ current, isCheckingMainUrlExistence: false });
          });
        }
      });
    } else {
      this.setState({ mainUrlValidationError: validationError.url });
    }
  }

  handleSendRequestForLoadingTime() {
    let mainUrl = this.normalizeUrl(this.state.mainUrl, this.state.mainUrlSchema);
    const regions = this.state.mainLocation ? [this.state.mainLocation, ...this.state.additionalLocations] : [];
    this.props
      .sendLoadingTimeRequest(mainUrl, regions)
      .finally(_ => this.setState({ current: this.state.current + 1 }));
  }

  validateCompetitors() {
    let competitorsValidation = [];
    let competitors = [...this.state.competitors];
    competitors.forEach(competitor => {
      if (!competitor["url"]) {
        return;
      }
      let url = this.normalizeUrl(competitor["url"], competitor["urlSchema"]);
      let competitorValidation = validate({ url: url }, urlValidationRule);
      if (competitorValidation) {
        competitorsValidation.push({ id: competitor["id"], validation: competitorValidation.url });
      }
    });

    // canMoveToNextStep checks if there are any validations errors for competitors
    const canMoveToNextStep = competitorsValidation.filter(competitor => competitor.validation).length === 0;
    const current = this.state.current + 1;
    canMoveToNextStep ? this.setState({ competitorsValidation, current }) : this.setState({ competitorsValidation });
  }

  getCompetitors(competitors, id) {
    let competitorsValidation = [...this.state.competitorsValidation];
    let i = competitorsValidation.findIndex(comp => comp.id === id);

    if (i > -1) {
      competitorsValidation[i] = { id: id, validation: undefined };
    }
    this.setState({ competitors, competitorsValidation });
  }

  handleSeoDbChange(seoDb) {
    this.setState({ seoDb });
  }

  changeMainLocation(value) {
    this.setState({ mainLocation: value, additionalLocations: [] });
  }

  changeAdditionalLocations(value) {
    this.setState({ additionalLocations: value });
  }

  getTreeData() {
    let additionalLocationsOrder = this.props.locationsOrder.filter(location => location !== this.state.mainLocation);
    return additionalLocationsOrder.map(regionCode => ({
      title: this.props.locations[regionCode],
      value: regionCode,
      key: regionCode,
      selectable: true,
      disabled:
        this.state.additionalLocations.length === this.props.maxRegionsNo - 1 &&
        !this.state.additionalLocations.includes(regionCode),
      isLeaf: true
    }));
  }

  handleKeywordsChange(event) {
    this.setState({ keywords: event.target.value });
  }

  handleStartOnboarding() {
    this.setState({ showStartScreen: false });
  }

  render() {
    const tProps = {
      allowClear: true,
      treeData: this.getTreeData(),
      value: this.state.additionalLocations,
      onChange: this.changeAdditionalLocations,
      treeCheckable: true,
      multiple: true,
      treeDefaultExpandAll: true,
      searchPlaceholder: "Select additional regions",
      dropdownStyle: {
        maxHeight: 400,
        overflow: "auto"
      },
      maxTagCount: this.props.maxRegionsNo - 1,
      filterTreeNode: true,
      treeNodeFilterProp: "title"
    };
    const { current } = this.state;
    return (
      <div className="add-tracked-website-wizard">
        <Card bordered={false} className="wizard-card">
          {this.state.showStartScreen ? (
            <OnboardingStartScreen handleStartOnboarding={this.handleStartOnboarding} />
          ) : (
            <React.Fragment>
              <Steps current={current}>
                {steps.map(item => (
                  <Step key={item} title={item} />
                ))}
              </Steps>
              <div className="steps-content">
                <Form className="step-form">
                  {this.state.current === 0 && (
                    <Form.Item
                      validateStatus={this.state.urlValidationError ? "error" : "validating"}
                      help={this.state.urlValidationError ? this.state.urlValidationError : ""}>
                      <div>
                        <h3>Main Website</h3>
                        <p>Track new website in order to have access to dashboard reports at any time</p>
                      </div>
                      <UrlInput
                        placeholder={"example.com"}
                        id={"main_url"}
                        url={this.state.mainUrl}
                        urlSchema={this.state.mainUrlSchema}
                        sendUrl={this.getMainUrl}
                        validationError={this.state.mainUrlValidationError}
                      />
                      {this.state.mainUrlExistenceError && (
                        <Alert message={this.state.mainUrlExistenceError} type="error" />
                      )}
                    </Form.Item>
                  )}

                  {this.state.current === 1 && (
                    <React.Fragment>
                      <Form.Item>
                        <div>
                          <h3>Target audience</h3>
                          <p>
                            The location of your primary target audience - our keyword tracking tool will retrieve
                            region specific data from the appropriate keyword database
                          </p>
                        </div>
                        <Select
                          value={this.state.seoDb}
                          onChange={this.handleSeoDbChange}
                          placeholder="Select target audience"
                          showSearch
                          onInputKeyDown={e => e.stopPropagation()}>
                          {this.props.regionalDatabases &&
                            Object.keys(this.props.regionalDatabases).map(regional_db => (
                              <Option key={regional_db} data-cy="change-db-select-option" value={regional_db}>
                                {regional_db} - {this.props.regionalDatabases[regional_db]}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                      <Form.Item>
                        <div>
                          <h3>Primary monitor location</h3>
                          <p>Please select the primary location from where we'll monitor your site's loading speed. </p>
                        </div>
                        <Select
                          value={this.state.mainLocation}
                          onChange={this.changeMainLocation}
                          placeholder="Select primary location"
                          showSearch
                          onInputKeyDown={e => e.stopPropagation()}>
                          {this.props.locationsOrder.map(regionCode => (
                            <Option value={regionCode} key={regionCode}>
                              {this.props.locations[regionCode]}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      {this.state.mainLocation && this.props.maxRegionsNo > 1 ? (
                        <Form.Item placeholder="Select regions">
                          <div>
                            <h3>Additional monitor locations</h3>
                            <p>
                              Please select {this.props.maxRegionsNo - 1} additional regions from where we'll monitor
                              your site's loading speed
                            </p>
                          </div>
                          <TreeSelect {...tProps} />
                        </Form.Item>
                      ) : null}
                    </React.Fragment>
                  )}

                  {this.state.current === 2 && (
                    <React.Fragment>
                      <div>
                        <h3>Add URLs for competitors of {this.state.mainUrl}</h3>
                        <p>Add up to {this.props.maxCompetitorsNo} competitors for side-by-side SEO comparison</p>
                      </div>
                      <Competitors
                        maxCompetitorsNo={this.props.maxCompetitorsNo}
                        sendCompetitors={this.getCompetitors}
                        competitors={this.state.competitors}
                        competitorsValidation={this.state.competitorsValidation}
                      />
                    </React.Fragment>
                  )}

                  {this.state.current === 3 && (
                    <Form.Item>
                      <div className="wizard-form-field">
                        {" "}
                        <h3 className="field-title">Add your keywords for {this.state.mainUrl}</h3>
                        <p className="field-description">
                          Add up to {this.props.maxKeywordsNo - this.props.keywordsCount} keywords to monitor your
                          site's placement in search engine results
                        </p>
                      </div>

                      <TextArea
                        value={this.state.keywords}
                        onChange={this.handleKeywordsChange}
                        placeholder="keyword1, keyword2, keyword3, etc"
                        autoSize={{ minRows: 5, maxRows: 25 }}
                        onKeyPress={e => e.stopPropagation()}
                        onKeyDown={e => e.stopPropagation()}
                        onKeyUp={e => e.stopPropagation()}
                      />
                    </Form.Item>
                  )}

                  {this.state.current === 4 && (
                    <div className="onboarding-start-screen">
                      <img src="/images/illustration-progress.svg" alt="finish onboarding" />
                      <h2>All done! We're setting up your account now...</h2>
                      <span className="description">This usually takes 30 seconds</span>
                      <span>
                        <Icon type="loading" />
                      </span>
                    </div>
                  )}
                </Form>
              </div>

              <div className="steps-action">
                {this.state.current === 0 && (
                  <FirstStepButtons
                    showSkip={this.props.showSkip}
                    isLoading={this.state.isCheckingMainUrlExistence}
                    handleSkipClick={this.redirectToDashboard}
                    handleNextClick={this.next}
                  />
                )}
                {this.state.current > 0 && this.state.current < 3 && (
                  <IntermediateStepButtons
                    handleBackClick={this.prev}
                    handleNextClick={this.next}
                    isLoading={this.state.current === 1 && this.props.isSendingLoadingTimeRequest}
                  />
                )}
                {this.state.current === 3 && (
                  <LastStepButtons
                    handleSubmit={this.handleSubmit}
                    isLoading={this.props.isAddingTrackedKeywords || this.props.isAddingTrackedWebsite}
                    handleBack={this.prev}
                  />
                )}
              </div>
            </React.Fragment>
          )}
        </Card>
      </div>
    );
  }
}

export default AddTrackedWebsiteWizard;
